'use client'

import { Cell, Spacer, Text } from '@vinted/web-ui'
import { useEffect, useRef } from 'react'

import { toggleOneTrustInfoDisplay } from '@marketplace-web/consent/consent-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { clickEvent, VanAdvertiserDto } from '@marketplace-web/ads/ads-data'

type Props = {
  vanCampaign: VanAdvertiserDto
}

const VanPrivacyPolicy = ({ vanCampaign }: Props) => {
  const translate = useTranslate('vinted_ads_network.privacy_policy')
  const ref = useRef<HTMLDivElement>(null)
  const { track } = useTracking()
  const { isWebview } = useSession()

  const { advertiser_name, paid_by } = vanCampaign

  useEffect(() => {
    const element = ref.current
    if (!element) {
      return undefined
    }

    const anchor = element.querySelector('a')
    if (!anchor) {
      return undefined
    }

    const handleClick = (event: MouseEvent) => {
      track(
        clickEvent({
          target: 'cookie_settings',
          screen: 'van_privacy_policy',
        }),
      )

      if (isWebview) {
        return
      }

      event.preventDefault()
      toggleOneTrustInfoDisplay()
    }

    anchor.addEventListener('click', handleClick)

    return () => {
      anchor.removeEventListener('click', handleClick)
    }
  }, [track, isWebview])

  return (
    <Cell styling={Cell.Styling.Wide}>
      <div className="van-privacy-policy">
        <Text type={Text.Type.Heading} as="h1" text={translate('title')} />
        <Spacer size={Spacer.Size.Medium} />
        <Text
          type={Text.Type.Body}
          as="p"
          text={
            paid_by
              ? translate('advertiser_and_payer_names', {
                  advertiser_name,
                  paid_by,
                })
              : translate('advertiser_name', { advertiser_name })
          }
          format
          html
        />
        <Spacer size={Spacer.Size.Medium} />
        <Text type={Text.Type.Body} as="div" text={translate('content')} html />
        <Spacer size={Spacer.Size.Medium} />
        <div ref={ref}>
          <Text type={Text.Type.Body} as="p" text={translate('settings')} html />
        </div>
      </div>
    </Cell>
  )
}

export default VanPrivacyPolicy
